import styled, { keyframes } from "styled-components";
import { AiOutlineCloseCircle } from "react-icons/ai"
import { useAdmin } from "../../../context/admin";
import { useEffect, useState } from "react";
import AdminService from "../../../services/admin.service";
import * as CommonTypes from '../../../types/common.types'
import Checkbox from "../../common/Checkbox";
import * as XLSX from 'xlsx';
import jsPDF from "jspdf";
import objectToArray from "../../../utils/objectToArray";
import { add } from "date-fns";
import { useGlobal } from "../../../context/global";
import removeTimezone from "../../../utils/removeTimezone";
import autoTable, { UserOptions } from "jspdf-autotable";

export default function Requirements() {
    const { setMessage, setIsLoading } = useGlobal()
    const {setRequirementsTripId, requirementsTripId, setCurrentDay } = useAdmin()
    const [trip, setTrip] = useState<CommonTypes.TripReservation>()
    useEffect(() => {
        async function getTrip() {
            if (!requirementsTripId) return
            setIsLoading(true)
            try {
                const data = (await AdminService.getTrip(requirementsTripId)).data
                setTrip(data)
            } catch (error) {
                console.log(error)
            } finally {
                setIsLoading(false)
            }
        }
        getTrip()
    }, [requirementsTripId]) 
    if (!trip) return <></>
    const tripCosts = trip.costs && trip.costs.map(item => item.value).reduce((a,b) => a + b , 0)
    
    const handleDownload = async (showSeller: boolean = false) => {
        // Change orientation to 'landscape'
        const doc = new jsPDF({
            orientation: 'landscape'
        });
        
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
    
        // Image dimensions and positioning
        const imageSize = 40; // Square size
        const imageX = 14; // Left margin
        const imageY = 15; // Top margin
    
        try {
            const imageUrl = '/assets/logo.png';
            const imagePadding = 2;
        
            doc.addImage(
                imageUrl, 
                'PNG', 
                imageX + imagePadding, 
                imageY + imagePadding, 
                imageSize - (imagePadding * 2), 
                imageSize - (imagePadding * 2)
            );
        
        } catch (error) {
            console.log('Could not load image:', error);
            doc.setFontSize(8);
            doc.setTextColor(150, 150, 150);
            doc.text('LOGO', imageX + imageSize/2, imageY + imageSize/2, { align: 'center', baseline: 'middle' });
            doc.setTextColor(0, 0, 0);
        }
        
        // Header table - use full width
        autoTable(doc, {
            startY: 15,
            body: [
                ['TOUR', trip.tour_id]
            ],
            theme: 'grid',
            styles: { 
                fontSize: 12, 
                cellPadding: 4,
                minCellHeight: 10
            },
            columnStyles: {
                0: { 
                    fontStyle: 'bold', 
                    cellWidth: pageWidth * 0.15, // 15% of page width
                    halign: 'center'
                },
                1: { 
                    cellWidth: ( pageWidth * 0.85 ) - 75, // Remaining width minus margins
                    halign: 'center',
                    fontStyle: 'bold',
                    fontSize: 14
                },
            },
            margin: { left: 60, right: 14 }
        });
    
        // Guide and driver information table - use full width with better distribution
        const tourData = [
            ['Nombre del guía:', trip.guider?.name, 'RUT:', trip.guider?.rut_passport],
            ['Nombre del chofer:', trip.driver?.name, 'RUT:', trip.driver?.rut_passport],
            ['Patente auto:', trip.car?.license_plate, 'Fecha:', removeTimezone(trip.trip_date!.toString())]
        ];
    
        const infoTableWidth = pageWidth - 75; // Full width minus margins
        autoTable(doc, {
            startY: 29, // Increased space after header
            body: tourData,
            theme: 'grid',
            styles: { 
                fontSize: 11, 
                cellPadding: 4,
                minCellHeight: 8,
            },
            columnStyles: {
                0: { 
                    fontStyle: 'bold', 
                    cellWidth: infoTableWidth * 0.2, // 20% of table width
                    halign: 'right'
                },
                1: { 
                    cellWidth: infoTableWidth * 0.3, // 30% of table width
                    halign: 'left'
                },
                2: { 
                    fontStyle: 'bold', 
                    cellWidth: infoTableWidth * 0.1, // 10% of table width
                    halign: 'right'
                },
                3: { 
                    cellWidth: infoTableWidth * 0.4, // 40% of table width
                    halign: 'left'
                },
            },
            margin: { left: 60, right: 14 },
        });
    
        // Main participants table - optimized for landscape
        const tableHeaders = [
            '#', 
            'NOMBRE', 
            'NACIONALIDAD', 
            'RUT/PASAPORTE', 
            'HOTEL', 
            'CONTACTO', 
            'EDAD', 
            'OBSERVACIONES'
        ];

        if (showSeller) {
            tableHeaders.splice(1, 0, '# group') // Insert 'VENDEDOR' after '#'
            tableHeaders.push('VENDEDOR')
        }
    
        const newData: any[] = []
        for (let i = 0; i < trip.reservations.length; i++) {
            const reservation = trip.reservations[i];
            if (showSeller) {
                newData.push([
                    i + 1, 
                    reservation.group_id?.slice(0, 3) || '', // Show only last 3 digits of group ID for brevity
                    reservation.passenger.name, 
                    reservation.passenger.nationality, 
                    reservation.passenger.rut_passport, 
                    reservation.passenger.hotel, 
                    reservation.passenger.phone, 
                    reservation.passenger.age, 
                    reservation.remark,
                    reservation.passenger.seller?.name
                ])
            } else {
                newData.push([
                    i + 1, 
                    reservation.passenger.name, 
                    reservation.passenger.nationality, 
                    reservation.passenger.rut_passport, 
                    reservation.passenger.hotel, 
                    reservation.passenger.phone, 
                    reservation.passenger.age, 
                    reservation.remark
                ])
            }
        }
    
        // Calculate column widths for main table to use full landscape width
        const mainTableWidth = pageWidth - 28; // Full width minus margins
        if (showSeller) {
            autoTable(doc, {
                startY: 75, // Adjusted position
                head: [tableHeaders],
                body: newData,
                theme: 'grid',
                styles: { 
                    fontSize: 9, // Slightly larger font since we have more space
                    cellPadding: 3,
                    minCellHeight: 6
                },
                headStyles: {
                    fillColor: [200, 200, 200],
                    textColor: 0,
                    fontStyle: 'bold',
                    fontSize: 10,
                    cellPadding: 4
                },
                columnStyles: {
                    0: { cellWidth: mainTableWidth * 0.03, halign: 'center' },  // 3% - #
                    1: { cellWidth: mainTableWidth * 0.06, halign: 'center' },  // 6% - #
                    2: { cellWidth: mainTableWidth * 0.15, halign: 'left' },    // 15% - NOMBRE
                    3: { cellWidth: mainTableWidth * 0.10, halign: 'left' },    // 10% - NACIONALIDAD
                    4: { cellWidth: mainTableWidth * 0.12, halign: 'left' },    // 12% - RUT/PASAPORTE
                    5: { cellWidth: mainTableWidth * 0.12, halign: 'left' },    // 12% - HOTEL
                    6: { cellWidth: mainTableWidth * 0.10, halign: 'left' },    // 10% - CONTACTO
                    7: { cellWidth: mainTableWidth * 0.07, halign: 'center' },  // 7% - EDAD
                    8: { cellWidth: mainTableWidth * 0.15, halign: 'left' },     // 15% - OBSERVACIONES
                    9: { cellWidth: mainTableWidth * 0.10, halign: 'left' }     // 10% - VENDEDOR
                },
                margin: { left: 14, right: 14 },
                tableWidth: 'wrap'
            });
        } else {
            autoTable(doc, {
                startY: 75, // Adjusted position
                head: [tableHeaders],
                body: newData,
                theme: 'grid',
                styles: { 
                    fontSize: 9, // Slightly larger font since we have more space
                    cellPadding: 3,
                    minCellHeight: 6
                },
                headStyles: {
                    fillColor: [200, 200, 200],
                    textColor: 0,
                    fontStyle: 'bold',
                    fontSize: 10,
                    cellPadding: 4
                },
                columnStyles: {
                    0: { cellWidth: mainTableWidth * 0.03, halign: 'center' },  // 3% - #
                    1: { cellWidth: mainTableWidth * 0.15, halign: 'left' },    // 15% - NOMBRE
                    2: { cellWidth: mainTableWidth * 0.10, halign: 'left' },    // 10% - NACIONALIDAD
                    3: { cellWidth: mainTableWidth * 0.12, halign: 'left' },    // 12% - RUT/PASAPORTE
                    4: { cellWidth: mainTableWidth * 0.20, halign: 'left' },    // 20% - HOTEL
                    5: { cellWidth: mainTableWidth * 0.10, halign: 'left' },    // 10% - CONTACTO
                    6: { cellWidth: mainTableWidth * 0.10, halign: 'center' },  // 10% - EDAD
                    7: { cellWidth: mainTableWidth * 0.20, halign: 'left' }     // 20% - OBSERVACIONES
                },
                margin: { left: 14, right: 14 },
                tableWidth: 'wrap'
            });
        }
    
        doc.save(`${trip.id}.pdf`);
    };

    const handleDownloadExcel = () => {
        const worksheetData = trip.reservations.map((reservation, index) => ({
            '#': index + 1,
            'GROUP': reservation.group_id?.slice(0, 3) || '',
            'NOMBRE': reservation.passenger.name,
            'NACIONALIDAD': reservation.passenger.nationality,
            'RUT/PASAPORTE': reservation.passenger.rut_passport,
            'HOTEL': reservation.passenger.hotel,
            'CONTACTO': reservation.passenger.phone,
            'EDAD': reservation.passenger.age,
            'OBSERVACIONES': reservation.remark,
            'SELLER': reservation.passenger.seller?.name || 'N/A'
        }));
    
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Trip Data');
        XLSX.writeFile(workbook, `${trip.id}.xlsx`);
    };

      async function closeTrip() {
        try {
            if (!requirementsTripId) return
            if (trip?.is_closed) return
            if (!tripCosts) return
            setIsLoading(true)
            const response = await AdminService.closeTrip(requirementsTripId)
            setMessage({type: 'success', content: response.data})
            setCurrentDay(curr => add(curr, {days: 1}))
            setCurrentDay(curr => add(curr, {days: -1}))
            setTimeout(() => {
                setMessage(undefined)
            }, 2500)
            setRequirementsTripId(undefined)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
      }
      // Reabrir: el trip vuelve a aparecer para asignar pasajeros. No toca las comisiones ya
      // generadas; al volver a cerrarlo solo las reservas nuevas generan la suya.
      async function reopenTrip() {
        try {
            if (!requirementsTripId) return
            if (!trip?.is_closed) return
            setIsLoading(true)
            const response = await AdminService.reopenTrip(requirementsTripId)
            setMessage({type: 'success', content: response.data})
            setCurrentDay(curr => add(curr, {days: 1}))
            setCurrentDay(curr => add(curr, {days: -1}))
            setTimeout(() => {
                setMessage(undefined)
            }, 2500)
            setRequirementsTripId(undefined)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
      }
    return (
        <Main>
            <Modal>
                <Checkbox
                checked={trip?.reservations ? trip?.reservations.length >= 20 : false}
                label={`Group filled ${trip?.reservations.length}/${20}`}
                />
                <Checkbox
                checked={tripCosts !== null || tripCosts === 0}
                label={`Trip costs $${tripCosts || 0}`}
                />
                <button type="submit"
                onClick={() => handleDownload(false)}
                >Download list</button>
                <button type="submit"
                onClick={() => handleDownloadExcel()}
                >
                    Download list alternative
                </button>
                {
                    <button type="submit"
                    onClick={trip.is_closed ? reopenTrip : closeTrip}
                    title={trip.is_closed ? 'The trip is closed. Reopen it to add passengers.' : undefined}
                    >
                        {
                            trip.is_closed ?
                            'Reopen trip'
                            :
                            'Close trip'
                        }
                    </button>
                }
                <div className="close" title="close"
                    onClick={() => {
                        setRequirementsTripId(undefined)
                    }}
                >
                    <AiOutlineCloseCircle/>
                </div>
            </Modal>
        </Main>
    )
}

const Main = styled.div`
z-index: 100;
position: fixed;
top: 0;
left: 0;
width: 100vw;
height: 100vh;
background-color: ${props => props.theme.blackTransparent};
`

const showUpModal = keyframes`
    from {
        top: 100%;
    }
    to {
        top: 50%;
    }
    `


const Modal = styled.div`
width: 80%;
max-height: 75%;
min-height: 400px;
overflow: hidden;
overflow-y: unset;
max-width: 500px;
position: absolute;
top: 50%; 
left: 50%;
transform: translate(-50%, -50%);
background-color: ${props => props.theme.white};
box-shadow: 0 0 20px ${props => props.theme.blackTransparent};
padding: 20px;
padding-top: 50px;
animation: ${showUpModal} .3s ease forwards;
display: flex;
align-items: center;
justify-content: center;
flex-direction: column;
gap: 20px;
.close {
    position: absolute;
    right: 20px;
    top: 20px;
    font-size: 22px;
    cursor: pointer;
}
button[type=submit] {
    display: inline-block;
    padding: 0 10px;
    height: 2.6em;
    line-height: 2.5em;
    overflow: hidden;
    font-size: 17px;
    z-index: 1;
    background-color: ${props => props.theme.white};
    color: ${props => props.theme.blue};
    border: 2px solid ${props => props.theme.blue};
    border-radius: 6px;
    position: relative;
    cursor: pointer;
}

button[type=submit]::before {
    position: absolute;
    content: "";
    background: ${props => props.theme.blue};
    width: calc(100% + 100px);
    height: 200px;
    z-index: -1;
    border-radius: 50%;
}

button[type=submit]:hover {
    color: ${props => props.theme.white};
}

button[type=submit]:before {
    top: 100%;
    left: 100%;
    transition: .3s all;
}

button[type=submit]:hover::before {
    top: -30px;
    left: -30%;
}
`